import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Card } from '../ui/card';

export const PaychecksArticle = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      question: "Why are construction paychecks often incorrect?",
      answer: "Late time entry, complex overtime structures, per diem inconsistencies, changing schedules, and lack of real-time visibility all contribute to paycheck errors in construction and refinery work."
    },
    {
      question: "How can workers prevent paycheck mistakes?",
      answer: "Using real-time pay tracking tools like PayFlow allows workers to see their pay building as they work, catch errors early, and verify hours, overtime, and per diem before payday."
    },
    {
      question: "What should I do if my construction paycheck is wrong?",
      answer: "Document your actual hours, overtime, and per diem immediately. Contact your supervisor or payroll department with specific details. Using PayFlow helps you maintain accurate records for disputes."
    },
    {
      question: "Can PayFlow help prevent paycheck errors?",
      answer: "Yes. PayFlow provides real-time visibility into hours, overtime calculations, and per diem tracking, helping you identify discrepancies before payday and maintain accurate records."
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Why Construction and Shutdown Paychecks Are Often Wrong",
    "description": "A detailed breakdown of why construction and refinery paychecks are often inaccurate, and how PayFlow provides workers real-time visibility into hours, overtime, and per diem.",
    "author": {
      "@type": "Organization",
      "name": "ProjectFlow Automations"
    },
    "keywords": "construction paycheck accuracy, shutdown payroll issues, overtime problems, per diem tracking, PayFlow app",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://projectflowautomations.com/knowledge/paychecks"
    }
  };

  return (
    <>
      <Helmet>
        <title>Why Construction Paychecks Are Often Wrong | PayFlow App</title>
        <meta 
          name="description" 
          content="Learn why construction and shutdown paychecks go wrong and how PayFlow gives workers clarity on hours, overtime, and per diem before payday." 
        />
        <meta name="keywords" content="construction paycheck accuracy, wrong paycheck construction, shutdown payroll issues, overtime errors, per diem mistakes, PayFlow app, refinery payroll visibility" />
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0D1115] pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Navigation */}
          <a
            href="/knowledge"
            className="inline-flex items-center text-[#30D5C8] hover:text-[#30D5C8]/80 font-['Open_Sans'] mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Knowledge Center
          </a>

          {/* Article Header */}
          <div className="mb-12">
            <div className="inline-block bg-[#30D5C8]/10 border border-[#30D5C8]/30 rounded-full px-4 py-2 mb-6">
              <span className="text-[#30D5C8] text-sm font-['Montserrat'] font-semibold">
                PAYROLL ACCURACY
              </span>
            </div>
            <h1 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-6 leading-tight">
              Why Construction and Shutdown Paychecks Are Often Wrong
            </h1>
            <p className="text-[#CBD3D9] text-xl font-['Open_Sans'] leading-relaxed">
              Construction and refinery workers experience paycheck issues more than almost any other industry. Not because the work isn't getting done — but because the systems tracking the work haven't kept up with the reality of the job.
            </p>
          </div>

          {/* Main Content */}
          <article className="prose prose-invert max-w-none">
            <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <AlertCircle className="text-[#30D5C8] flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-4">
                    Common Paycheck Issues
                  </h3>
                  <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">•</span>
                      <span>Missing hours</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">•</span>
                      <span>Incorrect overtime</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">•</span>
                      <span>Wrong rates</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">•</span>
                      <span>Missing per diem</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">•</span>
                      <span>Late corrections</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">•</span>
                      <span>No way to verify anything until payday</span>
                    </li>
                  </ul>
                </div>
              </div>
              <p className="text-[#CBD3D9] font-['Open_Sans'] italic">
                Most paycheck problems don't come from payroll errors alone. They come from the gap between the field and the office.
              </p>
            </Card>

            <div className="space-y-8">
              <section>
                <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">
                  The Real Causes of Incorrect Paychecks
                </h2>

                <div className="space-y-6">
                  <div className="bg-[#1A2733]/30 border border-[#506273]/20 rounded-lg p-6">
                    <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">
                      1. Late or Manual Time Entry
                    </h3>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">
                      Paper sheets, memory-based hours, end-of-week entry, and rushed approvals all introduce mistakes.
                    </p>
                  </div>

                  <div className="bg-[#1A2733]/30 border border-[#506273]/20 rounded-lg p-6">
                    <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">
                      2. Complex Overtime Structures
                    </h3>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] mb-4">
                      Tradespeople often deal with:
                    </p>
                    <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
                      <li className="flex items-start">
                        <span className="text-[#30D5C8] mr-3">•</span>
                        <span>OT after 8</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#30D5C8] mr-3">•</span>
                        <span>OT after 40</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#30D5C8] mr-3">•</span>
                        <span>Double time</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#30D5C8] mr-3">•</span>
                        <span>Weekend rules</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#30D5C8] mr-3">•</span>
                        <span>Shift premiums</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#30D5C8] mr-3">•</span>
                        <span>Multi-rate pay</span>
                      </li>
                    </ul>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] mt-4">
                      Most payroll systems weren't built for this level of complexity.
                    </p>
                  </div>

                  <div className="bg-[#1A2733]/30 border border-[#506273]/20 rounded-lg p-6">
                    <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">
                      3. Per Diem Inconsistencies
                    </h3>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">
                      Partial days, taxed vs. untaxed rules, travel days, and location-based adjustments cause confusion and disputes.
                    </p>
                  </div>

                  <div className="bg-[#1A2733]/30 border border-[#506273]/20 rounded-lg p-6">
                    <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">
                      4. Changing Schedules and Job Codes
                    </h3>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">
                      Shutdowns create moving targets. Workers change tasks, rates, and shifts — often multiple times in the same week.
                    </p>
                  </div>

                  <div className="bg-[#1A2733]/30 border border-[#506273]/20 rounded-lg p-6">
                    <h3 className="font-['Montserrat'] font-bold text-xl text-[#F5F7F8] mb-3">
                      5. No Real-Time Visibility
                    </h3>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed mb-3">
                      Workers don't know what their check will be until it lands.
                    </p>
                    <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold">
                      By that point, fixing mistakes is nearly impossible.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">
                  The Fix: Real-Time Pay Visibility
                </h2>
                <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#30D5C8]/30 p-8">
                  <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg leading-relaxed mb-6">
                    When workers can see their pay building as they work, everything changes:
                  </p>
                  <ul className="space-y-3 text-[#CBD3D9] font-['Open_Sans'] text-lg mb-6">
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">✓</span>
                      <span>Fewer disputes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">✓</span>
                      <span>Early corrections</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">✓</span>
                      <span>More trust</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">✓</span>
                      <span>Less stress</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#30D5C8] mr-3">✓</span>
                      <span>More accurate payroll</span>
                    </li>
                  </ul>
                  <p className="text-[#F5F7F8] font-['Montserrat'] font-bold text-xl">
                    Visibility isn't a luxury — it's a necessity.
                  </p>
                </Card>
              </section>

              {/* CTA to PayFlow */}
              <Card className="bg-gradient-to-br from-[#30D5C8]/20 to-[#30D5C8]/5 border-[#30D5C8]/50 p-8">
                <p className="text-[#F5F7F8] font-['Open_Sans'] text-lg leading-relaxed mb-6">
                  PayFlow by ProjectFlow Automations is built for construction and refinery workers who want clarity on hours, overtime, and per diem before payday.
                </p>
                <a
                  href="/payflow"
                  className="inline-block bg-[#30D5C8] text-[#0D1115] hover:bg-[#30D5C8]/90 font-['Montserrat'] font-bold text-lg px-8 py-4 rounded-lg transition-colors"
                >
                  Learn More About PayFlow
                </a>
              </Card>
            </div>
          </article>

          {/* FAQ Section */}
          <section className="mt-16">
            <h2 className="font-['Montserrat'] font-bold text-3xl text-[#F5F7F8] mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-6">
                  <h3 className="font-['Montserrat'] font-bold text-lg text-[#30D5C8] mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};
